import { RebaseTranslations } from "@rebasepro/cms-types";

/**
 * Spanish baseline translations for @rebasepro/app.
 *
 * This is the single source of truth for all default UI strings in Spanish.
 * Override any key via the `translations` prop on Rebase entry-point components.
 *
 * @group Core
 */
export const es: RebaseTranslations = {
    // ─── Form actions ────────────────────────────────────────────
    save: "Guardar",
    create: "Crear",
    create_copy: "Crear copia",
    save_and_close: "Guardar y cerrar",
    create_copy_and_close: "Crear copia y cerrar",
    create_and_close: "Crear y cerrar",
    discard: "Descartar",
    clear: "Limpiar",
    cancel: "Cancelar",

    // ─── Entity actions ──────────────────────────────────────────
    edit: "Editar",
    copy: "Copiar",
    delete: "Eliminar",
    delete_not_allowed: "Has seleccionado al menos una entidad que no puedes eliminar",
    edit_entity: "Editar",
    back_to_detail: "Volver a los detalles",

    // ─── Delete dialog ───────────────────────────────────────────
    delete_confirmation_title: "¿Eliminar?",
    delete_confirmation_body: "Esto eliminará la entidad. ¿Estás seguro?",
    delete_multiple_confirmation_body: "Esto eliminará las entidades seleccionadas. ¿Estás seguro?",

    // ─── Unsaved-changes dialog ───────────────────────────────────
    unsaved_changes_title: "Cambios sin guardar",
    unsaved_changes_body: "Tienes cambios sin guardar. ¿Quieres descartarlos?",
    discard_changes: "Descartar cambios",
    keep_editing: "Seguir editando",
    clear_form: "¿Vaciar formulario?",
    discard_changes_confirmation: "Todos los cambios no guardados se perderán. Esta acción no se puede deshacer.",
    clear_form_confirmation: "Todos los valores introducidos se borrarán. Esta acción no se puede deshacer.",

    // ─── Collection table / toolbar ──────────────────────────────
    search: "Buscar",
    find_by_id: "Buscar por ID",
    find_entity_by_id: "Buscar entidad por ID",
    filter: "Filtrar",
    filters: "Filtros",
    records_in_view: "Registros en esta vista",
    clear_filter: "Borrar filtro",
    clear_filter_sort: "Borrar filtro/orden",
    clear_sort: "Borrar orden",
    sort: "Ordenar",
    sort_by: "Ordenar por",
    sort_then_by: "Luego por",
    sort_ascending: "Ordenar ascendente",
    sort_descending: "Ordenar descendente",
    sort_remove: "Quitar orden",
    sort_move_up: "Dar más importancia a esta clave",
    sort_move_down: "Dar menos importancia a esta clave",
    sort_remove_key: "Quitar esta clave de orden",
    sort_key_position: "Clave de orden {{position}}",
    sort_shift_click_hint: "Mayús+clic para añadir una columna bajo el orden actual",
    save_entity_before_subcollections: "Debes guardar la entidad antes de añadir colecciones adicionales",
    clear_all: "Borrar todo",
    no_items: "Sin elementos",
    no_entries_found: "No se encontraron entradas",
    all_entries_loaded: "Todas las {{count}} entradas cargadas",
    create_your_first_entry: "Crea tu primera entrada",
    no_results_filter_sort: "No hay resultados con el filtro/orden aplicado",
    no_results_search: "No se encontraron resultados para \"{{search}}\"",
    add: "Añadir",
    remove: "Quitar",
    multiple_entities: "Múltiples entidades",
    unsaved_changes: "Tienes cambios sin guardar en esta colección {{collectionName}}.",
    so_empty: "Tan vacío...",
    no_results: "No hay resultados con el filtro/orden aplicado",
    refresh_data: "Actualizar datos",
    dark_mode: "Oscuro",
    light_mode: "Claro",
    system_mode: "Sistema",
    join_newsletter: "Únete a la newsletter, sin spam",
    ok: "Aceptar",
    save_collection_config: "Guardar la estructura de la colección",
    search_for_more_icons: "Buscar más iconos…",
    ai_modified: "Modificado por IA",
    size_label: "Tamaño",
    group_by: "Agrupar por",
    initialize_kanban_order: "Inicializar el orden para Kanban",
    copy_id: "Copiar ID",
    add_specific: "Añadir {{name}}",
    add_named: "Añadir \"{{name}}\"",
    select_specific: "Seleccionar {{name}}",
    select_from: "Seleccionar de {{name}}",
    done: "Hecho",
    log_out: "Cerrar sesión",
    column_cannot_be_edited: "Esta columna no se puede editar directamente",
    close: "Cerrar",
    hide_list: "Ocultar lista",
    show_list: "Mostrar lista",
    unsaved_local_changes: "Cambios locales sin guardar",
    unsaved_local_changes_description: "Este documento ha sido editado localmente y tiene cambios sin guardar. Estos cambios locales se perderán si no los aplicas.",
    preview_changes: "Previsualizar cambios",
    apply_changes: "Aplicar cambios",
    discard_local_changes: "Descartar cambios locales",
    preview_local_changes: "Previsualizar cambios locales",
    preview_local_changes_description: "Estos son los cambios locales que se aplicarán al formulario.",
    type: "Tipo",
    string: "cadena",
    number: "número",
    boolean: "booleano",
    date: "fecha",
    map: "mapa",
    array: "lista",
    arrays_of_arrays_not_supported: "Las listas de listas no están soportadas.",
    data_type_not_supported: "Tipo de datos {{dataType}} no soportado aún",
    passkey_error_unsupported: "Tu dispositivo o navegador no soporta passkeys.",

    // ─── Entity form feedback ─────────────────────────────────────
    saved_correctly: "Guardado correctamente",
    error_before_saving: "Error al preparar los datos",
    error_after_saving: "Error en el proceso posterior al guardado",
    error_saving_entity: "Error al guardar la entidad",
    entity_does_not_exist: "Esta entidad no existe en la base de datos",
    form_modified: "Este formulario ha sido modificado",
    form_in_sync: "El formulario actual está sincronizado con la base de datos",
    fix_errors_before_saving: "Corrija los errores resaltados antes de guardar",

    admin: "Admin",
    home: "Inicio",
    this_form_has_errors: "Este formulario tiene errores",
    error_loading_navigation: "Error cargando la navegación",
    error_loading_auth: "Error cargando la autenticación",
    this_form_has_been_modified: "Este formulario ha sido modificado",
    current_form_in_sync: "El formulario actual está sincronizado con la base de datos",
    open_in_console: "Abrir en consola",
    collection_does_not_exist: "La colección especificada no existe. Revisa la consola",
    unexpected_value: "Valor inesperado",
    unexpected_value_click_to_edit: "Valor inesperado. Haz clic para editar",
    unexpected_reference_value: "Valor de referencia inesperado. Haz clic para editar",
    copy_url_to_clipboard: "Copiar URL al portapapeles",
    open_image_in_new_tab: "Abrir imagen en una nueva pestaña",
    reference_not_set: "Referencia no establecida",
    reference_does_not_exist: "La referencia no existe",
    entity_not_found: "Entidad no encontrada",
    entity_not_found_body: "Este registro no existe, o puede estar oculto para ti por la seguridad a nivel de fila.",
    back_to_collection: "Volver a {{name}}",
    file_not_found: "Archivo no encontrado",
    unsaved_changes_in_entity: "Tienes cambios sin guardar en esta entidad.",
    delete_this_role: "Eliminar este rol",
    no_roles_yet: "Aún no tienes roles.",
    create_default_roles: "Crear roles por defecto",
    delete_role_confirmation: "¿Estás seguro de que quieres eliminar este rol?",
    delete_this_user: "Eliminar este usuario",
    no_users_yet: "Aún no hay usuarios",
    add_logged_user_as_admin: "Añadir al usuario autenticado como administrador",
    add_current_user_as_admin: "Añadir usuario actual como administrador",
    create_default_roles_and_add_admin: "Crear roles por defecto y añadir usuario actual como administrador",
    delete_user_confirmation: "¿Estás seguro de que quieres eliminar a este usuario?",
    create_your_users_and_roles: "Crea tus usuarios y roles",
    no_users_or_roles_defined: "No tienes usuarios ni roles. Puedes crear los roles por defecto y añadirte a ti mismo como administrador.",
    save_before_changing_schema: "Debes guardar el documento antes de cambiar el esquema",
    edit_schema_for_this_form: "Editar esquema para este formulario",
    no_permissions_to_edit_collection: "No tienes permisos para editar esta colección",
    browser_does_not_support_audio: "Tu navegador no soporta el elemento de audio.",
    user_not_found: "Usuario no encontrado: {{value}}",

    // ─── Collection view actions ──────────────────────────────────
    delete_selected: "Eliminar",
    cannot_delete_selected: "Has seleccionado al menos una entidad que no puedes eliminar",

    // ─── Array / field containers ─────────────────────────────────
    add_entry: "Añadir",
    add_on_top: "Añadir arriba",
    add_below: "Añadir debajo",
    add_to_field: "Añadir a {{fieldName}}",
    value: "Valor",
    key: "Clave",
    drag_drop_multiple: "Arrastra y suelta algunos archivos aquí, o haz clic para seleccionar archivos. Arrastra para reordenar.",
    drag_drop_single: "Arrastra y suelta un archivo aquí, o haz clic para seleccionar uno",

    // ─── Navigation / scaffold ────────────────────────────────────
    open_menu: "Abrir menú",
    close_drawer: "Cerrar panel",
    navigation_drawer: "Panel de navegación",
    collapse: "Contraer",
    expand: "Expandir",
    change_language: "Cambiar idioma",
    toggle_theme: "Cambiar tema",
    user_menu: "Menú de usuario",

    // ─── Error states ─────────────────────────────────────────────
    error: "Error",
    error_loading_data: "No se pudieron cargar los datos",
    error_check_server_logs: "Consulta los registros del servidor para más detalles.",
    error_technical_details: "Detalles técnicos",
    error_uploading_file: "Error al subir archivo",
    error_deleting: "Error al eliminar",
    error_before_delete: "Error antes de eliminar",
    error_updating_asset: "Error al actualizar recurso",
    error_deleting_asset: "Error al eliminar recurso",
    error_firestore_index: "Se requiere un índice de Firestore para esta consulta.",
    create_index: "Crear índice",
    value_is_not_reference: "El valor no es una referencia.",
    click_to_edit: "Haz clic para editar",
    data_is_not_array_of_references: "Los datos no son un array de referencias",

    // ─── Misc ─────────────────────────────────────────────────────
    loading: "Cargando",
    local_changes_applied: "Cambios locales aplicados al formulario",
    local_changes_discarded: "Cambios locales descartados",
    are_you_sure_leave: "¿Estás seguro de que quieres salir de esta página?",
    see_console_details: "Consulta la consola para más detalles.",
    drop_here_create_group: "Suelta aquí para crear un nuevo grupo",
    filter_for_null_values: "Filtrar por valores nulos",
    value_updated_elsewhere: "Este valor ha sido actualizado en otro lugar",
    add_property: "Añadir propiedad",
    edit_name: "Editar {{name}}",
    this_entity_not_exist: "Esta entidad no existe en la base de datos",
    internal_error: "ERROR Interno",

    // ─── Rename group dialog ──────────────────────────────────────
    rename_group: "Renombrar grupo",
    group_name_label: "Nombre del grupo",
    group_name_empty_error: "El nombre del grupo no puede estar vacío.",
    group_name_exists_error: "Este nombre de grupo ya existe.",

    // ─── Search ───────────────────────────────────────────────────
    search_collections: "Buscar colecciones",

    // ─── Navigation groups ────────────────────────────────────────
    views_group: "Vistas",

    // ─── Entity Edit View ─────────────────────────────────────────
    youd_need_to_save_before_additional_collections: "Debes guardar la entidad antes de añadir colecciones adicionales",

    // ─── Not Found Page ───────────────────────────────────────────
    page_not_found: "Página no encontrada",
    page_not_found_body: "Esta página no existe o podrías no tener acceso a ella",
    back_to_home: "Volver al inicio",

    // ─── Collection Editor ─────────────────────────────────────────
    default_collection_view: "Vista por defecto de la colección",
    table_view: "Tabla",
    cards_view: "Tarjetas",
    kanban_view: "Kanban",
    choose_how_entities_displayed_default: "Elige cómo se deben mostrar por defecto las entidades",

    document_view: "Vista de documento",
    side_panel: "Panel lateral",
    full_screen: "Pantalla completa",
    should_documents_opened_full_screen: "¿Quieres que los documentos se abran en pantalla completa o en un panel lateral?",

    select_custom_view: "Seleccionar vista personalizada",
    no_custom_views_defined: "No hay vistas personalizadas definidas. Define tus vistas en los ajustes de personalización, antes de usar este diálogo.",

    select_custom_action: "Seleccionar acción personalizada",
    no_custom_actions_defined: "No hay acciones personalizadas definidas. Define tus acciones en los ajustes de personalización, antes de usar este diálogo.",

    no_collections_found: "No se encontraron colecciones",
    start_building_collections: "Empieza a crear colecciones en Rebase fácilmente. Mapea la información a tu base de datos existente, importa desde archivos o utiliza nuestras plantillas.",
    create_first_collection: "Crea tu primera colección",
    define_collections_programmatically: "También puedes definir colecciones programáticamente.",
    edit_collection: "Editar colección",
    no_permissions_edit_collection: "No tienes permisos para editar esta colección",
    no_permissions_create_collection: "No tienes permisos para crear colecciones",
    create_collection: "Crear colección",
    update_collection: "Actualizar colección",

    new_collection: "Nueva colección",
    add_new_collection: "Añadir nueva colección",
    collection_with_name: "Colección {{name}}",
    change_icon: "Cambiar icono",
    is_subcollection_of: "Esta es una subcolección de",
    name: "Nombre",
    collection_name_description: "Nombre de esta colección (usualmente un nombre en plural, ej. 'Productos')",
    path: "Ruta",
    relative_path_to_parent: "Ruta relativa al nivel superior (no es necesario incluir la ruta padre)",
    path_in_database: "La ruta donde está almacenada esta colección en la base de datos",
    singular_name: "Nombre en singular",
    singular_name_description: "Opcionalmente define un nombre en singular para tus entidades",
    description: "Descripción",
    description_of_collection: "Descripción de la colección, puedes usar markdown",
    collection_id: "ID de la Colección",
    collection_id_description: "Este ID identifica a esta colección. Típicamente es el mismo que la ruta.",
    collection_group: "Grupo de colección",
    collection_group_description: "Un grupo de colección consiste en todas las colecciones con la misma ruta. Esto te permite realizar consultas sobre múltiples colecciones a la vez.",
    advanced_settings: "Ajustes avanzados",
    doc_history_global: "Historial de revisiones de documentos habilitado si está activado globalmente",
    doc_history_enabled: "Historial de revisiones de documentos HABILITADO",
    doc_history_not_enabled: "Historial de revisiones de documentos NO habilitado",
    doc_history_description: "Al estar habilitado, cada documento de esta colección tendrá un registro de cambios. Esto es útil para fines de auditoría. Los datos se almacenan en una subcolección del documento en tu base de datos __history.",
    document_id_generation: "Generación de IDs de documentos",
    code_defined: "Definido por código",
    users_must_define_id: "Los usuarios deben definir un ID",
    users_can_define_id: "Los usuarios pueden definir un ID, pero no es obligatorio",
    doc_id_auto_generated: "El ID del documento se genera automáticamente",
    config_doc_id_generation: "Configura cómo se generan los IDs de los documentos al crear nuevas entidades.",
    enable_text_search: "Habilitar búsqueda de texto para esta colección",
    text_search_description: "Permite buscar texto en esta colección. Si no has especificado un mecanismo de búsqueda de texto, utilizará la búsqueda local incorporada. Esto no se recomienda para colecciones grandes, ya que puede incidir en el rendimiento.",
    database_id: "ID de la base de datos",
    default_text: "(por defecto)",

    custom_actions: "Acciones personalizadas",
    more_info: "Más información",
    define_custom_actions_cli: "Define tus propias acciones personalizadas subiéndolas con la CLI.",
    action_defined_in_code: "Esta acción está definida en el código con la clave",
    add_custom_entity_action: "Añadir acción personalizada de entidad",
    remove_this_action: "¿Eliminar esta acción?",
    remove_action_warning: "Esto no eliminará ningún dato, sólo se retirará del panel de administración",

    subcollections_of: "Subcolecciones de",
    add_subcollection: "Añadir subcolección",
    custom_views: "Vistas personalizadas",
    define_custom_views_cli: "Define tus propias vistas personalizadas subiéndolas con la CLI.",
    view_defined_in_code: "Esta vista se define en el código con la clave",
    add_custom_entity_view: "Añadir vista de entidad personalizada",
    delete_this_subcollection: "¿Eliminar esta subcolección?",
    remove_collection_warning: "Esto no eliminará ningún dato, sólo la colección en el panel de administración",
    remove_this_view: "¿Eliminar esta vista?",
    remove_view_warning: "Esto no eliminará ningún dato, sólo la vista en el panel de administración",

    no_collection_selected: "Ninguna colección seleccionada",
    code_for_collection: "Código para",
    use_config_define_json: "Usa esta configuración para definir la colección en formato JSON.",
    customise_collection_code: "Si deseas personalizar la colección en código, puedes añadir este código a la configuración de tu aplicación de administración.",
    copied: "Copiado",

    property_cant_be_edited: "Esta propiedad no se puede editar",
    property_not_editable_description: "Puede que no tengas permisos para editarla o que esté definida en código y no se pueda modificar.",
    delete_this_property: "¿Eliminar esta propiedad?",
    delete_property_warning: "Esto no eliminará datos, sólo modificará la configuración de la colección.",
    error_must_specify_id: "Debes especificar un id para el campo",
    error_id_format: "El id sólo puede contener letras, números y guiones bajos (_), y no puede empezar con un número",
    error_id_already_exists: "Ya existe otro campo con este ID",
    error_must_specify_title: "Debes especificar un título para el campo",
    custom_or_other: "Personalizado/Otro",
    select_property_widget: "Selecciona un widget de propiedad",
    error_changing_data_type: "Este widget utiliza un tipo de dato diferente al seleccionado inicialmente. Esto puede generar errores con los datos existentes.",
    required: "Requerido",

    enum_form_dialog: "Diálogo de formulario Enum",
    imported_data_preview: "Vista previa de datos importados",
    entities_with_same_id_overwritten: "Las entidades con el mismo id se sobrescribirán",
    collection_editor: "Editor de colecciones",
    properties_in_this_group: "Propiedades en este grupo",
    data_property_mapping: "Mapeo de propiedades",
    property_edit_view: "Vista de edición de propiedad",
    all_of_these: "Todos estos",
    any_of_these: "Cualquiera de estos",

    only_admins_edit_roles: "Sólo los administradores pueden editar roles",
    error_user_not_found: "Usuario no encontrado",
    all_roles: "Todos los roles",
    role: "Rol",
    name_of_this_role: "Nombre del rol",
    id_of_this_role: "ID del rol",
    create_entities: "Crear entidades",
    read_entities: "Leer entidades",
    update_entities: "Actualizar entidades",
    delete_entities: "Eliminar entidades",
    all_collections: "Todas las colecciones",
    create_entities_in_collections: "Crear entidades en colecciones",
    access_all_data_in_every_collection: "Acceder a todos los datos en cada colección",
    update_data_in_any_collection: "Actualizar datos en cualquier colección",
    delete_data_in_any_collection: "Eliminar datos en cualquier colección",
    allow_all_permissions_in_this_collections: "Permitir todos los permisos en estas colecciones",
    all: "Todo",
    customise_permissions_description: "Puedes personalizar los permisos de los usuarios para este rol en cada colección",
    create_collections: "Crear colecciones",
    yes: "Sí",
    no: "No",
    can_user_create_collections: "¿Puede el usuario crear colecciones?",
    edit_collections: "Editar colecciones",
    only_own_collections: "Sólo las propias",
    own: "Propias",
    can_user_edit_collections: "¿Puede el usuario editar colecciones?",
    delete_collections: "Eliminar colecciones",
    can_user_delete_collections: "¿Puede el usuario eliminar colecciones?",
    error_saving_role: "Hubo un error al guardar este rol",
    create_role: "Crear rol",
    update: "Actualizar",

    only_admins_change_roles: "Sólo los administradores pueden cambiar roles",
    must_be_at_least_one_admin: "Tiene que haber al menos un administrador",
    logged_user_not_found: "Usuario autenticado no encontrado",
    user: "Usuario",
    user_id: "ID del usuario",
    name_of_this_user: "Nombre de este usuario",
    email_of_this_user: "Correo electrónico de este usuario",
    roles: "Roles",
    create_user: "Crear usuario",

    users: "Usuarios",
    add_user: "Añadir usuario",
    add_role: "Añadir rol",
    is_admin: "Es Administrador",
    default_permissions: "Permisos predeterminados",
    created_on: "Creado en",
    email: "Email",
    id: "ID",
    read: "Leer",

    column_in_file: "Columna en el archivo",
    map_to_property: "Mapear a Propiedad",
    default_values: "Valores por defecto",
    default_values_description: "Puedes seleccionar un valor por defecto para columnas no mapeadas o valores en blanco:",
    property: "Propiedad",
    default_value: "Valor predeterminado",
    autogenerate_id: "Autogenerar ID",
    id_column_description: "Columna que se utilizará como ID de cada documento",
    do_not_set_value: "No asignar valor",
    set_value_to_true: "Asignar valor verdadero (true)",
    set_value_to_false: "Asignar valor falso (false)",
    drag_and_drop_file: "Arrastra y suelta un archivo aquí, o haz clic para subirlo",
    error_saving_data: "Error al guardar los datos",
    retry: "Reintentar",
    saving_data: "Guardando datos",
    entities_saved: "entidades guardadas",
    do_not_close_tab: "No cierres esta pestaña o se interrumpirá la importación",
    import: "Importar",
    import_data: "Importar datos",
    upload_file_description: "Sube un archivo CSV, Excel o JSON y mapéalo a tu esquema actual",
    back: "Atrás",
    next: "Siguiente",
    save_data: "Guardar datos",
    use_column_as_id: "Usar esta columna como ID",
    do_not_import_property: "No importar esta propiedad",
    entities_will_be_overwritten: "Las entidades con el mismo ID se sobrescribirán",
    data_imported_successfully: "Datos importados correctamente",
    export: "Exportar",
    export_data: "Exportar datos",
    download_table_csv: "Descargar el contenido de esta tabla como CSV",
    csv: "CSV",
    json: "JSON",
    dates_as_timestamps: "Fechas como marcas de tiempo (timestamps)",
    dates_as_strings: "Fechas como cadenas (strings)",
    flatten_arrays: "Aplanar arrays",
    download: "Descargar",
    large_number_of_documents: "Esta colección posee un gran número de documentos ({{count}}).",
    too_many_documents_to_export: "Esta colección tiene más documentos ({{count}}) de los que el navegador puede exportar en un solo archivo (límite {{limit}}). Fíltrala antes de exportar.",
    include_undefined_values: "Incluir valores omitidos (undefined)",
    submit: "Enviar",

    no_filterable_properties: "No hay propiedades filtrables disponibles",
    apply_filters: "Aplicar filtros",

    // ─── Filter Presets ──────────────────────────────────────────
    filter_presets: "Preajustes",
    filter_preset_apply: "Aplicar preajuste",
    filter_preset_active: "Activo: {{label}}",

    list: "Lista",
    table_view_mode: "Tabla",
    cards: "Tarjetas",
    board: "Tablero",
    initialize_kanban_order_desc: "Esto asignará valores secuenciales para todos los elementos sin ordenación. Los elementos mantendrán su orden actual en cada columna.",
    kanban_view_not_available: "La vista Kanban no está disponible",
    kanban_view_requires_enum: "La vista Kanban requiere una propiedad de tipo texto con opciones (enum) para poder agrupar. Por favor, asegúrate de añadir una propiedad de este tipo a la colección para utilizar esta vista.",
    no_enum_values_configured: "No hay opciones (enum) configuradas para la propiedad \"{{property}}\"",
    items_need_backfill: "Existen elementos sin valores de ordenación. Inicialízalos para habilitar la reorganización con arrastrar y soltar.",
    kanban_order_not_configured: "El ordenamiento por arrastrar y soltar no está configurado. Establece una propiedad de orden en la configuración de la colección para persistir las posiciones de las tarjetas.",
    initialize: "Inicializar",
    confirm_multiple_delete: "¿Confirmar eliminación múltiple?",
    delete_entity_confirm_title: "¿Confirmas que quieres borrar el elemento '{{entityName}}'?",
    add_existing: "Añadir existente",
    unlink_entity_confirm_title: "¿Quitar este/a {{entityName}} de este registro?",
    unlink_entity_confirm_body: "Seguirá en {{collectionName}} y disponible para otros registros.",
    confirm_multiple_unlink: "¿quitarlos de este registro?",
    no_filter: "Sin filtro",
    is_true: "es verdadero",
    is_false: "es falso",
    error_uploading_file_size: "Error al subir el archivo: supera los {{maxSize}} bytes",
    error_uploading_file_type: "Error al subir el archivo: el tipo de archivo no es compatible",
    drag_drop_files: "Arrastra archivos aquí o haz clic para editarlos",
    drag_drop_file: "Arrastra un archivo aquí o haz clic para editarlo",
    multiple_deleted: "Múltiples eliminados",
    some_entities_deleted: "Algunas entidades han sido eliminadas, pero no todas",
    error_deleting_entities: "Error al eliminar entidades",
    deleted: "eliminado",
    selection_options: "Opciones de selección",
    selection_menu_all_loaded: "Todo en esta página",
    selection_menu_all_matching: "Los {{total}} {{collection}}",
    selection_menu_none: "Ninguno",
    selection_select_all_loaded: "Seleccionar las filas cargadas",
    selection_deselect_all: "Deseleccionar todo",
    selection_reading_rows: "Leyendo filas… {{loaded}} de {{total}}",
    selection_reading_rows_unknown: "Leyendo filas… {{loaded}} hasta ahora",
    selection_deleting_progress: "Eliminados {{completed}} de {{total}}",
    confirm_delete_selection: "¿Eliminar {{total}} {{collection}}?",
    confirm_delete_selection_unknown: "¿Eliminar todas las filas que coinciden con el filtro actual?",
    confirm_delete_selection_body: "Esta acción no se puede deshacer. Cada fila se elimina por separado, así que puede tardar un rato.",

    export_selection_count: "Descargar las {{total}} filas seleccionadas en {{format}}",
    export_selection_all_matching: "Descargar todos los {{collection}} que coinciden con el filtro actual",

    select_reference: "Seleccionar referencia",
    select_references: "Seleccionar referencias",
    account_settings: "Configuración de la cuenta",
    profile: "Profile",
    security: "Seguridad",
    sessions: "Sesiones",
    change_password: "Cambiar Contraseña",
    current_password: "Contraseña Actual",
    new_password: "Nueva Contraseña",
    confirm_password: "Confirmar Nueva Contraseña",
    password_changed: "Contraseña cambiada con éxito. Se cerrará tu sesión.",
    passwords_dont_match: "Las contraseñas no coinciden",
    password_too_short: "La contraseña debe tener al menos 8 caracteres",
    password_change_not_available: "El cambio de contraseña no está disponible para cuentas que usan proveedores de inicio de sesión externos.",
    changing_password: "Cambiando...",
    display_name: "Nombre visible",
    photo_url: "URL de la foto",
    save_profile: "Guardar perfil",
    saving: "Saving...",
    no_active_sessions: "No se encontraron sesiones activas.",
    revoking: "Revoking...",
    revoke_all_sessions: "Revocar todas las sesiones",
    unknown_device: "Dispositivo desconocido",
    current: "Current",
    role_id: "ID del rol",
    role_name: "Nombre del rol",
    add_reference: "Añadir referencia",
    add_reference_to: "Añadir referencia a {{name}}",

    /** RolesView — table & permissions */
    role_deleted_successfully: "Rol eliminado correctamente",
    error_deleting_role: "Error al eliminar el rol",
    collection_permissions: "Permisos de la colección",
    collection: "Collection",
    no_collections_configured: "No hay colecciones configuradas",
    no_security_rules_defined: "No hay reglas de seguridad definidas: todas las operaciones están sin restricciones",
    no_rules: "sin reglas",

    /** UsersView — snackbar */
    bootstrap_admin_success: "¡Ya eres administrador! Actualizando...",
    failed_to_bootstrap_admin: "No se pudo crear el administrador inicial",
    user_deleted_successfully: "Usuario eliminado correctamente",
    error_deleting_user: "Error al eliminar el usuario",
    user_created_successfully: "Usuario creado correctamente",
    invitation_sent: "Se ha enviado un correo de invitación a {{email}}. Puede usar el enlace para establecer su contraseña.",
    invitation_sent_title: "Invitación enviada",
    temporary_password: "Contraseña temporal",
    temporary_password_description: "El correo no está configurado. Comparte esta contraseña temporal con el usuario de forma segura. No se volverá a mostrar.",
    temporary_password_email_failed_description: "No se pudo entregar el correo. Comparte esta contraseña temporal con el usuario de forma segura. No se volverá a mostrar.",
    copy_password: "Copiar contraseña",
    password_copied: "Contraseña copiada al portapapeles",

    /** UsersView — pagination & search */
    search_users: "Buscar usuarios",
    created: "Creado",
    actions: "Acciones",
    no_users_found: "No se encontraron usuarios",
    reset_password: "Restablecer contraseña",
    reset_password_success: "Contraseña restablecida correctamente",
    reset_password_confirmation: "¿Seguro que quieres restablecer la contraseña de este usuario?",
    reset_password_send_email: "Enviar un correo para restablecer la contraseña",
    reset_password_set_manually: "Establecer una contraseña manualmente",
    reset_password_set_manually_description: "La contraseña se actualiza de inmediato y no se envía ningún correo. Compártela con el usuario de forma segura.",

    /** Permission-denied empty states */
    no_permission_to_view_users: "No tienes permisos para ver los usuarios",
    no_permission_to_view_roles: "No tienes permisos para ver los roles",
    no_permission_description: "Contacta a un administrador si necesitas acceso a esta sección.",
    error_resetting_password: "Error al restablecer la contraseña",

    /** Editor table-bubble */
    add_row_before: "Insertar fila antes",
    add_row_after: "Insertar fila después",
    delete_row: "Eliminar fila",
    add_column_before: "Insertar columna antes",
    add_column_after: "Insertar columna después",
    delete_column: "Eliminar columna",
    delete_table: "Eliminar tabla",

    /** Editor image-bubble */
    alt_text: "Texto alternativo",
    title: "Title",

    /** MapFieldBinding */
    a_property_of_this_map_has_error: "Una propiedad de este mapa contiene un error",

    /** AI Collection Generator Popover */
    generate_collection_with_ai: "Generar colección con IA",
    modify_collection_with_ai: "Modificar colección con IA",
    describe_collection_to_create: "Describe la colección que quieres crear.",
    describe_changes_to_make: "Describe los cambios que quieres hacer en esta colección.",
    ai_placeholder_create: "p. ej., Crear una colección de productos con nombre, precio, descripción y categoría...",
    ai_placeholder_modify: "p. ej., Agregar un campo de imagen en miniatura con almacenamiento, hacer el precio obligatorio...",
    ai_assist: "Asistente IA",
    generating: "Generando...",

    /** Recently extracted strings for collection editor */
    this_is_subcollection_of: "Esta es una subcolección de",
    use_existing_paths_database: "Usa una de las rutas existentes en tu base de datos:",
    describe_collection_ai: "Describe tu colección a la IA:",
    generate_with_ai: "Generar con IA",
    create_from_json_config: "Crear desde configuración JSON:",
    paste_json_config: "Pegar configuración JSON",
    create_collection_from_file_formats: "Crear una colección desde un archivo (csv, json, xls, xslx...)",
    select_template: "Seleccionar una plantilla:",
    products: "Productos",
    collection_products_subtitle: "Una colección de productos con imágenes, precios y existencias",
    collection_users_subtitle: "Una colección de usuarios con correos electrónicos, nombres y roles",
    blog_posts: "Entradas de blog",
    collection_blog_posts_subtitle: "Una colección de artículos con imágenes, autores y contenido complejo",
    pages: "Páginas",
    collection_pages_subtitle: "Una colección de páginas con imágenes, autores y contenido complejo",
    continue_from_scratch: "Continuar desde cero",

    /** Admin views config */
    admin_users: "Usuarios del panel de administración",
    roles_menu: "Roles",
    project_settings: "Ajustes del proyecto",

    by_signing_in_you_agree_to_our: "Al iniciar sesión aceptas nuestros",
    terms_and_conditions: "Términos y Condiciones",
    and_our: "y nuestra",
    privacy_policy: "Política de Privacidad",
    email_password: "Email/Contraseña",
    sign_in_with_google: "Iniciar sesión con Google",

    // --- Auth error messages ---
    auth_user_not_found: "Usuario no encontrado",
    auth_wrong_password: "Contraseña incorrecta. Por favor, inténtalo de nuevo.",
    auth_user_disabled: "Usuario deshabilitado. Por favor, contacta con soporte.",
    auth_account_exists_with_different_credential: "Ya existe una cuenta con un método de inicio de sesión diferente",
    auth_email_already_in_use: "El correo electrónico ya está en uso",
    auth_google_permissions_required: "Necesitas conceder permisos adicionales para gestionar tus proyectos de Google Cloud",
    auth_invalid_email_password: "Por favor introduce tanto el correo electrónico como la contraseña",
    auth_enter_email_first: "Por favor introduce tu correo electrónico primero",
    auth_password_reset_sent: "Correo electrónico de restablecimiento de contraseña enviado",
    auth_sign_in_account: "Inicia sesión en tu cuenta",
    auth_create_new_account: "Crea una cuenta nueva",
    auth_password: "Contraseña",
    auth_reset_password: "Restablecer contraseña",
    auth_new_user: "¿Usuario nuevo?",
    auth_have_account: "¿Tienes una cuenta?",
    auth_sign_in: "Iniciar Sesión",
    auth_sign_up: "Regístrate",


    auto_setup_collections_button: "Configurar colecciones automáticamente",
    auto_setup_collections_title: "¿Configurar colecciones automáticamente?",
    auto_setup_collections_desc: "Esto creará automáticamente la configuración de las colecciones que <b>NO</b> estén mapeadas",
    this_can_take_a_minute: "Esto puede tardar un minuto o dos",
    no_collections_found_to_setup: "No se encontraron colecciones para configurar",
    collections_have_been_setup: "¡Tus colecciones han sido configuradas!",
    error_setting_up_collections: "Error al configurar colecciones",

    // --- Home Suggestions ---

    admin_panel_ready_bring_data: "Tu panel de administración está listo. Traigamos tus datos.",
    admin_panel_ready_get_started: "Tu panel de administración está listo. Así es como puedes empezar.",
    auto_detect_collections: "Autodetectar colecciones",
    auto_detect_collections_desc: "Deja que la IA escanee tu base de datos y genere esquemas de colección automáticamente.",
    create_a_collection: "Crear una colección",
    create_collection_desc: "Define manualmente tu primera colección desde cero usando el editor visual.",
    read_the_docs: "Consulta la documentación",
    read_the_docs_desc: "Aprende cómo personalizar campos, vistas, acciones y más.",
    explore_docs: "Explorar la documentación",
    want_to_customize_with_code: "¿Quieres personalizar con código? Ejecuta",
    to_scaffold_a_local_project: "para crear un proyecto local.",

    // ─── Collection Editor — Validation ──────────────────────────
    validation: "Validación",
    unique: "Único",
    required_message: "Mensaje de campo obligatorio",
    required_tooltip: "No podrás guardar esta entidad si este valor no está establecido",
    unique_tooltip: "No puede haber múltiples entidades con el mismo valor",
    lowercase: "Minúsculas",
    uppercase: "Mayúsculas",
    trim: "Recortar espacios",
    exact_length: "Longitud exacta",
    min_length: "Longitud mínima",
    max_length: "Longitud máxima",
    matches_regex: "Coincide con regex",
    not_valid_regexp: "No es una expresión regular válida",
    regex_helper: "ej. /^\\d+$/ solo para dígitos",
    min_value: "Valor mínimo",
    max_value: "Valor máximo",
    less_than: "Menor que",
    more_than: "Mayor que",
    positive_value: "Valor positivo",
    negative_value: "Valor negativo",
    integer_value: "Valor entero",

    // ─── Collection Editor — Property Edit ───────────────────────
    invalid_regular_expression: "Expresión regular no válida",
    must_specify_target_collection: "Debes especificar una colección de destino para el campo",
    need_specify_repeat_field: "Debes especificar un campo de repetición",
    need_specify_block_properties: "Debes especificar las propiedades de este bloque",
    incomplete_condition: "Condición incompleta - por favor selecciona un campo",
    field_name: "Nombre del campo",

    // ─── Collection Editor — Display & Config ────────────────────
    kanban_column_property: "Propiedad de columna Kanban",
    select_a_property: "Seleccionar una propiedad",
    kanban_property_not_found: "La propiedad \"{{property}}\" no existe o no es una propiedad de tipo string con enum. Selecciona una propiedad válida o borra la selección.",
    no_enum_string_properties: "No se encontraron propiedades de tipo string con enum. Agrega una propiedad string con enumValues para usar la vista Kanban.",
    kanban_column_description: "Selecciona una propiedad de tipo string con valores enum para agrupar entidades en columnas",
    create_property: "+ Crear propiedad \"{{property}}\"",
    order_property: "Propiedad de orden",
    order_property_not_found: "La propiedad \"{{property}}\" no existe o no es una propiedad numérica. Selecciona una propiedad válida o borra la selección.",
    no_number_properties: "No se encontraron propiedades numéricas. Agrega una propiedad numérica para habilitar el ordenamiento.",
    order_property_description: "Selecciona una propiedad numérica para mantener el orden de los elementos",
    display_settings: "Configuración de visualización",
    default_row_size: "Tamaño de fila predeterminado",
    side_dialog_width: "Ancho del diálogo lateral",
    side_dialog_width_description: "Opcionalmente define el ancho (en píxeles) del diálogo lateral de entidades. El valor predeterminado es 768px",
    inline_editing_enabled: "Los datos se pueden editar directamente en la vista de tabla",
    inline_editing_disabled: "Los datos solo se pueden editar en la vista de formulario",
    inline_editing_description: "Permitir editar datos directamente en la vista de tabla, sin abrir la vista de formulario.",
    include_json_view: "Incluir vista JSON",
    no_json_view: "No incluir vista JSON",
    json_view_description: "Incluir la representación JSON del documento.",
    not_found_suffix: "no encontrado",

    // ─── Editor ─────────────────────────────────────────────────
    editor_text: "Texto",
    editor_text_description: "Empieza a escribir con texto plano.",
    editor_heading_1: "Encabezado 1",
    editor_heading_1_description: "Encabezado de sección grande.",
    editor_heading_2: "Encabezado 2",
    editor_heading_2_description: "Encabezado de sección mediano.",
    editor_heading_3: "Encabezado 3",
    editor_heading_3_description: "Encabezado de sección pequeño.",
    editor_todo_list: "Lista de tareas",
    editor_todo_list_description: "Sigue tus tareas con una lista de tareas.",
    editor_bullet_list: "Lista con viñetas",
    editor_bullet_list_description: "Crea una lista simple con viñetas.",
    editor_numbered_list: "Lista numerada",
    editor_numbered_list_description: "Crea una lista con numeración.",
    editor_quote: "Cita",
    editor_quote_description: "Captura una cita.",
    editor_code: "Código",
    editor_code_description: "Captura un fragmento de código.",
    editor_image: "Imagen",
    editor_image_description: "Sube una imagen desde tu ordenador.",
    editor_multiple: "Múltiple",
    editor_link: "Enlace",
    editor_save: "Guardar",
    editor_cancel: "Cancelar",
    editor_remove_link: "Eliminar enlace",
    editor_paste_or_type_link: "Pega o escribe el enlace",
    editor_open_in_new_window: "Abrir en una ventana nueva",
    editor_bold: "Negrita",
    editor_italic: "Cursiva",
    editor_underline: "Subrayado",
    editor_strikethrough: "Tachado",
    editor_autocomplete: "Autocompletar",
    editor_autocomplete_description: "Añade texto basado en el contexto.",

    // ─── Text Search Dialog ─────────────────────────────────────
    text_search_dialog_title: "Activar búsqueda de texto",
    text_search_local_not_recommended: "La búsqueda de texto local no es recomendable para colecciones grandes.",
    text_search_local_fetch_warning: "Ten en cuenta que activar la búsqueda de texto local requiere descargar todos los documentos de la colección y almacenarlos en el navegador. Esto puede ser ineficiente para colecciones grandes y generar costes adicionales.",
    text_search_external_suggestion: "Para colecciones más grandes, te recomendamos usar un motor de búsqueda externo como Algolia o Elastic Search, y asignar un delegado de búsqueda a tu configuración.",
    text_search_local_description: "La búsqueda de texto local es la forma más sencilla de activar la búsqueda de texto en tu colección. Carga todos los documentos de la colección en el navegador y realiza la búsqueda de forma local. Es la opción recomendada para colecciones pequeñas.",
    text_search_own_implementation: "Has implementado tu propio controlador de búsqueda de texto. Puedes activar la búsqueda de texto para tu colección.",
    text_search_enable_for_collection: "Activar para esta colección",
    text_search_enable_for_project: "Activar para el proyecto",
    text_search_enabled_snackbar: "Búsqueda de texto local activada",

    settings_heading: "Ajustes",
    settings_project_name: "Nombre del proyecto",
    settings_default_language: "Idioma predeterminado",
    settings_default_language_caption: "Selecciona el idioma base para este proyecto. Los usuarios pueden modificar esta preferencia en sus ajustes personales.",
    settings_enable_local_text_search: "Habilitar búsqueda de texto local",
    settings_local_text_search_caption: "Habilita la búsqueda de texto local para todas las colecciones. Esto te permitirá buscar campos de texto en tus colecciones usando la barra de búsqueda de Rebase. Ten en cuenta que esta función puede implicar un mayor número de lecturas, ya que indexará todos los campos de texto de tus colecciones.",
    settings_doc_history_all_collections: "Historial de documentos habilitado para todas las colecciones",
    settings_doc_history_caption: "Cuando está activado, todas las colecciones tendrán el historial habilitado por defecto. Puedes cambiar este ajuste en cada colección. El historial se guardará en la subcolección __history de cada documento.",
    settings_theme: "Tema",
    settings_primary_color: "Color primario",
    settings_secondary_color: "Color secundario",
    settings_sample_theme_components: "Componentes de ejemplo del tema",
    settings_drag_drop_logo: "Arrastra y suelta tu logotipo aquí",
    settings_security_rules: "Reglas de seguridad",
    settings_security_rules_description: "Rebase utiliza las reglas de seguridad de Firebase para restringir el acceso a los datos. Al crear un nuevo usuario a través de Rebase, se añade el atributo personalizado rebaseUser al usuario en el proyecto cliente. Al añadir las siguientes reglas de seguridad a tu proyecto, te aseguras de que los usuarios de Rebase puedan acceder a los datos a través de Rebase.",
    settings_security_rules_add_domain: "Recuerda añadir el dominio {{domain}} a los dominios permitidos de tu proveedor",
    settings_security_rules_caption: "Estas reglas restringen el acceso a los datos solo para usuarios de Rebase, pero no aplican los permisos a nivel de base de datos. Los permisos se aplican en el frontend, lo que funciona bien en la mayoría de los proyectos. Si necesitas aplicar los permisos a nivel de base de datos, puedes modificar estas reglas de seguridad. Los roles asignados a un usuario se configuran como atributos personalizados en el token de autenticación de Firebase, por lo que puedes usarlos en tus reglas de seguridad.",

    // ─── Studio: SQL Editor ─────────────────────────────────────
    studio_sql_executing_query: "EJECUTANDO CONSULTA...",
    studio_sql_query_error: "Error en la consulta",
    studio_sql_run_query_placeholder: "Ejecuta una consulta para ver los resultados",
    studio_sql_visual_execution_plan: "Plan de ejecución visual",
    studio_sql_success: "SUCCESS",
    studio_sql_no_results: "La consulta se ejecutó correctamente pero no devolvió resultados.",
    studio_sql_rows: "Rows:",
    studio_sql_time: "Time:",
    studio_sql_copy_markdown: "Copiar como Markdown",
    studio_sql_export_json: "Exportar JSON",
    studio_sql_export_csv: "Exportar CSV",
    studio_sql_format_sql: "Formatear SQL",
    studio_sql_explain: "Explain",
    studio_sql_limit_1000: "Limit 1000",
    studio_sql_remove_from_favorites: "Quitar de favoritos",
    studio_sql_add_to_favorites: "Añadir a favoritos",
    studio_sql_save: "Save",
    studio_sql_run: "Run",
    studio_sql_database: "Database",
    studio_sql_role: "Role",
    studio_sql_admin: "(Admin)",
    studio_sql_select_db: "Elegir base de datos",
    studio_sql_query_results: "Resultados de la consulta",
    studio_sql_save_snippet: "Guardar fragmento SQL",
    studio_sql_snippet_name: "Nombre del fragmento",
    studio_sql_snippet_name_placeholder: "p. ej., Obtener todos los usuarios",
    studio_sql_snippet_saved_local: "Se guardará en tu almacenamiento local.",
    studio_sql_cancel: "Cancel",
    studio_sql_dangerous_operation: "Operación peligrosa detectada",
    studio_sql_dangerous_operation_body: "La consulta que vas a ejecutar contiene operaciones potencialmente destructivas (DELETE, DROP, TRUNCATE o UPDATE sin WHERE). ¿Seguro que quieres continuar?",
    studio_sql_snippet_saved: "Fragmento «{{name}}» guardado.",
    studio_sql_markdown_copied: "¡Resultados copiados como Markdown!",
    studio_sql_markdown_copy_failed: "No se pudo copiar el Markdown al portapapeles.",
    studio_sql_row_updated: "Fila actualizada correctamente.",
    studio_sql_cannot_edit_missing_query: "No se pueden editar las filas: falta la consulta subyacente.",
    studio_sql_cannot_resolve_table: "No se pudo determinar la tabla ni la clave primaria",
    studio_sql_missing_pk: "A la fila le faltan columnas de clave primaria: {{columns}}. No se puede actualizar con seguridad.",
    studio_sql_update_failed: "Error al actualizar: {{message}}",
    studio_sql_execution_not_supported: "La fuente de datos actual no admite la ejecución de SQL.",
    studio_sql_error_executing: "Se produjo un error al ejecutar la consulta.",
    studio_sql_error_explaining: "Se produjo un error al explicar la consulta.",
    studio_sql_save_first_to_favorite: "Guarda el fragmento antes de marcarlo como favorito.",
    studio_sql_collections_label: "Collections:",
    studio_sql_admin_collections_tooltip: "Tables in this query that are mapped as admin collections",
    studio_sql_edit_entity: "Edit {{name}} #{{id}}",
    studio_sql_sql_not_supported: "Esta fuente de datos no admite la ejecución de SQL",
    studio_sql_fetch_error: "No se pudieron obtener las opciones de conexión: {{message}}",
    studio_sql_unexpected_format: "Formato de datos inesperado: {{type}}",
    studio_sql_no_tables: "No se encontraron tablas en esta base de datos.",
    studio_sql_schema_fetch_error: "No se pudo obtener el esquema: {{message}}",

    // ─── Studio: SQL Editor Sidebar ──────────────────────────────
    studio_sql_sidebar_snippets: "Snippets",
    studio_sql_sidebar_history: "History",
    studio_sql_sidebar_schema: "Schema",
    studio_sql_sidebar_no_snippets: "No hay fragmentos guardados",
    studio_sql_sidebar_save_snippet_hint: "Guarda una consulta para acceder a ella aquí.",
    studio_sql_sidebar_no_history: "Sin historial",
    studio_sql_sidebar_history_hint: "Las consultas ejecutadas aparecerán aquí.",
    studio_sql_sidebar_delete_snippet: "Eliminar fragmento",
    studio_sql_close_tab: "Cerrar esta pestaña de consulta",
    studio_sql_new_tab: "Nueva pestaña de consulta",

    // ─── Studio: Schema Browser ──────────────────────────────────
    studio_schema_tables: "Tables",
    studio_schema_loading: "Cargando esquema...",
    studio_schema_no_tables: "No se encontraron tablas",
    studio_schema_retry: "Retry",
    studio_schema_primary_key: "Clave primaria",
    studio_schema_select_all: "SELECT * FROM",
    studio_schema_insert_into: "INSERT INTO",
    studio_schema_update: "UPDATE",
    studio_schema_delete_from: "DELETE FROM",
    studio_schema_columns: "{{count}} columnas",

    // ─── Studio: RLS Editor ──────────────────────────────────────
    studio_rls_title: "Seguridad a nivel de fila",
    studio_rls_description: "Gestiona las políticas RLS de tus tablas",
    studio_rls_enabled: "RLS activada",
    studio_rls_disabled: "RLS desactivada",
    studio_rls_no_rls: "Sin RLS",
    studio_rls_enable_rls: "Activar RLS",
    studio_rls_disable_rls: "Desactivar RLS",
    studio_rls_create_policy: "Crear política",
    studio_rls_policies: "Policies",
    studio_rls_no_policies: "No hay políticas definidas",
    studio_rls_no_policies_desc: "Esta tabla no tiene políticas RLS. El acceso a todas las filas depende del estado de RLS.",
    studio_rls_add_first_policy: "Añade tu primera política",
    studio_rls_force_rls: "Forzar RLS",
    studio_rls_force_rls_desc: "Si se activa, las políticas RLS se aplican también al propietario de la tabla.",
    studio_rls_enable_force_rls: "Activar RLS forzada",
    studio_rls_disable_force_rls: "Desactivar RLS forzada",
    studio_rls_edit: "Edit",
    studio_rls_delete: "Delete",
    studio_rls_confirm_delete_title: "Eliminar política",
    studio_rls_confirm_delete_body: "¿Seguro que quieres eliminar esta política? Esta acción no se puede deshacer.",
    studio_rls_confirm_enable_title: "Activar la seguridad a nivel de fila",
    studio_rls_confirm_enable_body: "Al activar RLS, el acceso a las filas de esta tabla quedará restringido por tus políticas. Asegúrate de tener políticas adecuadas.",
    studio_rls_confirm_disable_title: "Desactivar la seguridad a nivel de fila",
    studio_rls_confirm_disable_body: "Al desactivar RLS, todas las filas quedarán accesibles para todos los roles. ¿Seguro que quieres continuar?",
    studio_rls_select_table: "Selecciona una tabla",
    studio_rls_no_tables: "No hay tablas disponibles",
    studio_rls_error: "Error al cargar las políticas",
    studio_rls_retry: "Retry",
    studio_rls_loading: "Cargando políticas...",

    // ─── Studio: Policy Editor ───────────────────────────────────
    studio_policy_edit: "Editar política",
    studio_policy_create: "Crear una política nueva",
    studio_policy_defining_rules: "Definiendo reglas de acceso para",
    studio_policy_cancel: "Cancel",
    studio_policy_save: "Guardar política",
    studio_policy_template: "Empezar desde una plantilla (opcional)",
    studio_policy_select_template: "Selecciona una plantilla...",
    studio_policy_name: "Nombre de la política",
    studio_policy_name_placeholder: "Da a tu política un nombre descriptivo",
    studio_policy_behavior: "Comportamiento de la política",
    studio_policy_permissive: "Permissive",
    studio_policy_permissive_desc: "Se combina con «OR»",
    studio_policy_restrictive: "Restrictive",
    studio_policy_restrictive_desc: "Se combina con «AND»",
    studio_policy_command: "Comando de la política",
    studio_policy_target_roles: "Roles destinatarios",
    studio_policy_roles_placeholder: "Selecciona roles (por defecto, public)",
    studio_policy_using_expr: "Expresión USING",
    studio_policy_using_expr_desc: "Se aplica a las filas existentes (SELECT, UPDATE, DELETE)",
    studio_policy_check_expr: "Expresión WITH CHECK",
    studio_policy_check_expr_desc: "Se aplica a las filas nuevas o modificadas (INSERT, UPDATE)",
    studio_policy_help_title: "Guía de seguridad a nivel de fila (RLS)",
    studio_policy_help_intro: "La seguridad a nivel de fila (RLS) te permite restringir a qué filas puede acceder o qué filas puede modificar cada usuario según su estado de autenticación o sus roles.",
    studio_policy_help_step1_title: "1. Selecciona una plantilla",
    studio_policy_help_step1_desc: "Si es tu primera vez con RLS, lo más fácil es empezar eligiendo una plantilla predefinida en el desplegable. Configurará la política automáticamente para casos habituales como «los usuarios pueden actualizar sus propias filas».",
    studio_policy_help_step2_title: "2. Roles destinatarios",
    studio_policy_help_step2_desc: "Elige a qué usuarios se aplica la política:",
    studio_policy_help_role_public: "Se aplica a todo el mundo (con sesión iniciada o sin ella).",
    studio_policy_help_role_authenticated: "Solo se aplica a los usuarios con sesión iniciada.",
    studio_policy_help_role_anon: "Solo se aplica a los usuarios que NO han iniciado sesión.",
    studio_policy_help_step3_title: "3. Expresión USING",
    studio_policy_help_step3_desc: "Una condición SQL que debe evaluarse como verdadera para que las filas existentes sean visibles o actualizables (SELECT, UPDATE, DELETE).",
    studio_policy_help_step3_example: "Garantiza que los usuarios solo vean las filas de las que son propietarios.",
    studio_policy_help_step4_title: "4. Expresión WITH CHECK",
    studio_policy_help_step4_desc: "Una condición SQL que debe evaluarse como verdadera para que se guarden las filas nuevas o modificadas (INSERT, UPDATE).",
    studio_policy_help_step4_example: "Suele coincidir con la expresión USING cuando los usuarios solo pueden insertar o editar sus propios datos.",
    studio_policy_help_auth_vars_title: "Variables de autenticación de Rebase",
    studio_policy_help_auth_vars_desc: "Rebase ofrece funciones auxiliares nativas que puedes usar en tus expresiones:",
    studio_policy_help_auth_uid: "Devuelve el ID del usuario actual como texto. Ejemplo:",
    studio_policy_help_auth_jwt: "Devuelve la carga útil completa del JWT como JSONB para que puedas comprobar reclamaciones personalizadas. Ejemplo:",
    studio_policy_help_auth_roles: "Devuelve los ID de rol del usuario como una cadena separada por comas. Se usa mejor con:",
    studio_policy_help_docs_cta: "Para saber más sobre las políticas de Postgres, consulta la documentación oficial.",
    studio_policy_help_read_docs: "Leer la documentación",
    studio_policy_help_got_it: "¡Entendido!",

    // ─── Studio: UI Actions ──────────────────────────────────────
    studio_add_kanban_column: "Añadir columna",
    studio_add_kanban_column_title: "Añadir columna al Kanban",
    studio_add_kanban_column_name: "Nombre de la columna",
    studio_add_kanban_column_name_placeholder: "Nombre de la nueva columna",
    studio_add_kanban_column_add: "Add",
    studio_add_kanban_column_cancel: "Cancel",
    studio_collection_view_sql: "Editor SQL",
    studio_collection_view_admin: "Vista de administración",
    studio_editor_collection_tooltip: "Editar el esquema de la colección",
    studio_editor_collection_no_permission: "No tienes permiso para editar esta colección",
    studio_editor_collection_start_tooltip: "Copiar la ruta o editar el esquema",
    studio_editor_collection_start_no_permission: "No tienes permiso para modificar esta colección",
    studio_editor_collection_start_copied: "Copied!",
    studio_editor_entity_tooltip: "Editar el esquema de la entidad",
    studio_editor_entity_no_permission: "No tienes permiso para editar esta entidad",
    studio_home_edit_collection: "Editar colección",
    studio_home_delete_collection: "Eliminar colección",
    studio_home_confirm_delete_title: "¿Eliminar la colección?",
    studio_home_confirm_delete_body: "¿Seguro que quieres eliminar la colección «{{name}}»? Esta acción no se puede deshacer.",
    studio_kanban_setup: "Configuración del Kanban",
    studio_missing_reference: "Referencia inexistente: {{path}}",
    studio_missing_reference_create: "Create",
    studio_new_collection: "Colección nueva",
    studio_new_collection_card: "Colección nueva",
    studio_new_collection_no_permission: "No tienes permisos para crear colecciones",
    studio_property_add_column: "Añadir una propiedad nueva",
    studio_property_add_column_no_permission: "No tienes permiso para añadir propiedades nuevas",
    studio_add_kanban_column_desc: "Añade una opción nueva a la propiedad «{{property}}». Se creará una columna nueva en el tablero Kanban.",
    studio_add_kanban_column_adding: "Adding...",
    studio_collection_view_edit: "Edit",
    studio_editor_collection_disabled: "La edición de colecciones está desactivada",
    studio_editor_collection_edit: "Editar colección",
    studio_editor_entity_save_first: "Debes guardar el documento antes de cambiar el esquema",
    studio_editor_entity_edit_schema: "Editar el esquema de este formulario",
    studio_editor_collection_start_save_filter: "Guardar el filtro y el orden por defecto",
    studio_editor_collection_start_clear_filter: "Borrar el filtro y el orden por defecto",
    studio_editor_collection_start_reset_filter: "Restablecer el filtro y el orden por defecto",
    studio_editor_collection_start_saved: "Configuración por defecto guardada",
    studio_home_duplicate_collection: "Duplicate",
    studio_home_delete: "Delete",
    studio_home_confirm_delete_no_data: "No se eliminará ningún dato, solo la colección en el panel de administración",
    studio_home_collection_deleted: "Colección eliminada",
    studio_kanban_configure: "Configurar el Kanban",
    studio_missing_reference_error: "No hay ninguna colección para la ruta: {{path}}",
    studio_new_collection_add: "Añadir nueva colección",

    // ─── Property field labels ──────────────────────────────────
    db_column_type: "Tipo de columna de base de datos",
    primary_key_unique_id: "Clave primaria / ID único",
    spread_children_as_columns: "Expandir hijos como columnas",
    mode: "Modo",
    timezone: "Zona horaria",
    target_collection: "Colección destino",

    // ─── Storage property ───────────────────────────────────────
    storage_file_name: "Nombre del archivo",
    storage_path: "Ruta de almacenamiento",
    storage_max_size: "Tamaño máximo (en bytes)",
    storage_resize_mode: "Modo de redimensionamiento",
    storage_output_format: "Formato de salida",
    storage_max_width: "Ancho máximo (px)",
    storage_max_height: "Alto máximo (px)",
    storage_quality: "Calidad (0-100)",
    storage_file_upload_config: "Configuración de subida de archivos",
    storage_image_resize_config: "Configuración de redimensionamiento de imagen",
    storage_all_file_types: "Todos los tipos de archivo permitidos",
    storage_allowed_file_types: "Tipos de archivo permitidos",
    storage_include_bucket_url: "Incluir URL del bucket (s3://...) en el valor guardado",
    storage_save_url: "Guardar URL en lugar de la ruta de almacenamiento",

    // ─── DateTime property ──────────────────────────────────────
    datetime_automatic_value: "Valor automático",

    // ─── Markdown property ──────────────────────────────────────
    markdown_paste_behavior: "Comportamiento al pegar",

    // ─── Entity identity bar and inspector ──────────────────────
    entity_saved: "Guardado",
    more_actions: "Más acciones",
    record_history: "Historial",
    inspect_record: "Inspeccionar registro",
    inspect: "Inspeccionar",
    close_inspector: "Cerrar el inspector",
    open_in_live_site: "Abrir en el sitio publicado",

    // ─── Revision history ───────────────────────────────────────
    history_revert: "Volver a esta versión",
    history_revert_title: "¿Volver a esta versión de los datos?",
    history_reverted: "Se volvió a la versión seleccionada",
    history_revert_error: "Error al revertir el registro",
    history_revert_dirty: "Guarda o descarta tus cambios antes de revertir",
    history_empty: "No hay historial disponible",
    history_empty_description: "Al guardar un registro se crea una nueva versión y se almacena en el historial.",
    history_new_entity: "El historial solo está disponible para registros existentes",
    history_no_more: "No hay más historial",

    // ─── Studio: tools, groups and empty states ─────────────────
    // The admin's own three groups, read by `useNavigationGroupLabel` the same
    // way as the Studio ones below — the group *name* is an identifier the
    // ordering and the icons key off, and only the heading is translated.
    studio_group_views: "Vistas",
    studio_group_admin: "Administración",
    studio_group_settings: "Ajustes",
    studio_group_database: "Base de datos",
    studio_group_compute: "Cómputo",
    studio_group_storage: "Almacenamiento",
    studio_group_api: "API",
    studio_group_access_control: "Control de acceso",
    studio_tool_sql: "Consola SQL",
    studio_tool_sql_description: "Ejecuta consultas SQL",
    studio_tool_js: "Consola JS",
    studio_tool_js_description: "Ejecuta JavaScript",
    studio_tool_rls: "Políticas RLS",
    studio_tool_rls_description: "Seguridad a nivel de fila",
    studio_tool_storage: "Almacenamiento",
    studio_tool_storage_description: "Gestiona los archivos de almacenamiento",
    studio_tool_cron: "Tareas cron",
    studio_tool_cron_description: "Gestiona las tareas programadas",
    studio_tool_schema_visualizer: "Visualizador de esquema",
    studio_tool_schema_visualizer_description: "Diagrama ER interactivo de la base de datos",
    studio_tool_branches: "Ramas",
    studio_tool_branches_description: "Crea y gestiona ramas de la base de datos",
    studio_tool_backups: "Copias de seguridad",
    studio_tool_backups_description: "Descarga copias de seguridad de la base de datos",
    studio_tool_api: "Explorador de API",
    studio_tool_api_description: "Documentación de API interactiva y pruebas",
    studio_tool_logs: "Explorador de registros",
    studio_tool_logs_description: "Registros de sistema y consultas en tiempo real",
    studio_tool_api_keys: "Claves de API",
    studio_tool_api_keys_description: "Crea y gestiona claves de API con alcance",
    studio_backups_denied_title: "No puedes listar las copias de seguridad de este proyecto",
    studio_backups_denied_hint: "El proyecto está bien. Listar copias de seguridad requiere una conexión de administrador, y la cuenta con la sesión iniciada fue rechazada.",
    studio_backups_read_failed: "No se han podido leer las copias de seguridad de este proyecto",
    studio_backups_downloading: "Descargando…",
    studio_backups_roles_file: "Archivo de roles",
    studio_backups_roles_file_hint: "El .globals.sql guardado con esta copia. Recrea los roles de base de datos a los que se refieren los permisos y la seguridad a nivel de fila del volcado, y `rebase db restore` lo lee junto al .dump, así que guarda los dos archivos juntos.",
    studio_backups_no_roles_file: "Sin archivo de roles",
    studio_backups_no_roles_file_hint: "Esta copia no tiene ningún .globals.sql. Al restaurarla en un Postgres nuevo, se detiene en el primer permiso concedido a un rol de base de datos que allí no existe.",
    studio_cron_denied_title: "No puedes listar las tareas cron de este proyecto",
    studio_cron_denied_hint: "El proyecto está bien. La API de cron es solo para administradores, y la cuenta con la sesión iniciada fue rechazada.",
    studio_cron_read_failed: "No se han podido leer las tareas cron de este proyecto",
    studio_api_keys_denied_title: "No puedes listar las claves de API de este proyecto",
    studio_api_keys_denied_hint: "El proyecto está bien. Gestionar claves de API es una operación de administrador, y la cuenta con la sesión iniciada fue rechazada.",
    studio_api_keys_read_failed: "No se han podido leer las claves de API de este proyecto",
    studio_api_keys_empty_title: "Aún no hay claves de API",
    studio_api_keys_empty_hint: "Cree una clave para habilitar la autenticación entre servicios",
    studio_api_keys_revoke: "Revocar",
    studio_api_keys_select_hint: "Seleccione una clave de API para ver los detalles",
    studio_api_keys_permissions: "Permisos",
    studio_api_keys_no_permissions: "No hay permisos configurados",
    studio_api_keys_revoke_confirmation: "Confirmación de revocación",
    studio_api_keys_name_label: "Nombre:",
    studio_api_keys_access_label: "Acceso:",
    studio_api_keys_admin_granted: "Rol de administrador concedido",
    studio_api_keys_admin_granted_hint: "las rutas de administración y las políticas default_admin",
    studio_api_keys_done: "Listo",
    studio_api_keys_status_active: "Activa",
    studio_api_keys_status_expired: "Caducada",
    studio_api_keys_status_revoked: "Revocada",
    studio_api_keys_stat_status: "Estado",
    studio_api_keys_stat_last_used: "Último uso",
    studio_api_keys_stat_expires: "Caduca",
    studio_api_keys_never: "Nunca",
    studio_api_keys_role_service: "Servicio",
    studio_api_keys_stat_rate_limit: "Límite de peticiones",
    studio_api_keys_rate_limit_default: "Predeterminado (1000/15 min)",
    studio_api_keys_stat_created_by: "Creada por",
    studio_api_keys_copy_failed: "No se pudo copiar",
    studio_branches_denied_title: "No puedes listar las ramas de este proyecto",
    studio_branches_denied_hint: "El proyecto está bien. El uso de ramas va por la conexión de administrador, y la cuenta con la sesión iniciada fue rechazada.",
    studio_branches_read_failed: "No se han podido leer las ramas de este proyecto",
    studio_schema_no_collections: "No hay colecciones declaradas",
    studio_schema_no_collections_body: "Todavía no hay nada que dibujar. Añade una colección en config/collections/, o abre «Editar colecciones» para añadir una aquí.",
    studio_schema_introspect_hint: "Contra una base de datos que ya tiene tablas, rebase schema introspect escribe los archivos de colección por ti.",


    // ─── Studio: RLS editor confirmations ───────────────────────
    studio_rls_enable_confirm_title: "¿Activar la seguridad a nivel de fila?",
    studio_rls_enable_confirm_body: "Hasta que una política lo permita, ninguna fila de {{table}} será legible. Postgres deniega por defecto.",
    studio_rls_disable_confirm_title: "¿Desactivar la seguridad a nivel de fila?",
    studio_rls_disable_confirm_body: "Todas las políticas de {{table}} dejan de aplicarse. Quien pueda alcanzar la tabla podrá leer y escribir todas las filas.",
    studio_rls_drop_policy_title: "¿Eliminar esta política?",
    studio_rls_drop_policy_body: "{{policy}} se elimina de {{table}} en la base de datos. No se escribe nada en tu código, así que un despliegue puede restaurarla.",
    studio_rls_unapplied: "Sin aplicar",
    studio_rls_unapplied_tooltip: "Declarada en tu código, todavía no en la base de datos. Guárdala aquí para planificar y aplicar el cambio, o ejecuta `rebase db push`.",


    // ─── Studio: empty states that name a remedy ────────────────
    studio_branches_unavailable_title: "Las ramas de base de datos no están disponibles",
    studio_branches_unavailable_body: "Las ramas necesitan un backend Postgres con una conexión de administrador. Define ADMIN_CONNECTION_STRING en el entorno del backend.",
    studio_backups_not_configured_title: "Las copias de seguridad no están configuradas",
    studio_backups_not_configured_body: "Define BACKUP_DESTINATION como una ruta local o una URL s3:// o gs://, y crea una copia con `rebase db backup` o un cron programado.",
    studio_cron_empty_title: "No hay tareas cron registradas",
    studio_cron_empty_body: "Añade un archivo en backend/crons/ que exporte por defecto una CronJobDefinition.",
    studio_read_the_docs: "Consulta la documentación"
};
